/**
 * @file Web gallery collage upsell.
 */

import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import sampleSize from 'lodash.samplesize';
import useInterval from 'use-interval';

import withModal from '../../../../components/global/modal/withModal';
import { WEB_GALLERY_COLLAGE_UPSELL_MODAL } from '../../../../constants/modals';
import Button from '../../../../components/global/Button';
import CollagePreviewTemplate from '../../../../components/global/custom-prints/CollagePreviewTemplate';
import arrayReplaceItem from '../../../../util/array-replace-item';
import getRandomInt from '../../../../util/random-int';
import ContextualString from '../../../../components/contextual/ContextualString';
import { getAlbumCustomPrintName } from '../../../../util/custom-prints';
import MemoryMatePreviewTemplate from '../../../../components/global/custom-prints/MemoryMatePreviewTemplate';

const propTypes = {
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string,
    album: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      thumbnail_urls: PropTypes.array,
    }),
  ),
  onDismiss: PropTypes.func.isRequired,
  shuffle: PropTypes.bool,
  onMount: PropTypes.func,
  onConfirm: PropTypes.func,
};

const WebGalleryCollageUpsell = ({
  albumCustomPrint = {},
  photos = [],
  onDismiss,
  shuffle,
  onMount = () => {},
  onConfirm = () => {},
}) => {
  const previewImageUrl = get(
    albumCustomPrint,
    'custom_print.preview_image.url',
  );
  const photosNo = albumCustomPrint.photos_no;
  const album = get(albumCustomPrint, 'album') || {};
  const [samplePhotos, setSamplePhotos] = useState(
    sampleSize(photos, photosNo),
  );

  useEffect(() => {
    onMount();
  }, []);

  useInterval(() => {
    if (!shuffle) {
      return;
    }

    setSamplePhotos(prevSamplePhotos => {
      return arrayReplaceItem(
        prevSamplePhotos,
        getRandomInt(0, prevSamplePhotos.length - 1),
        sampleSize(photos, 1)[0],
      );
    });
  }, 2500);

  return (
    <Fragment>
      <div className="web-gallery-collage-upsell-preview-img-wrap">
        {previewImageUrl && !samplePhotos.length && (
          <img
            src={previewImageUrl}
            alt=""
            className="web-gallery-collage-upsell-preview-img"
          />
        )}

        {samplePhotos.length > 0 && (
          <Fragment>
            {photosNo === 2 ? (
              <MemoryMatePreviewTemplate
                className="web-gallery-collage-upsell-preview-img"
                collage={{
                  ...albumCustomPrint,
                  photos: {
                    total: samplePhotos.length,
                    records: samplePhotos.map((photo, index) => ({
                      position: index,
                      photo,
                    })),
                  },
                }}
              />
            ) : (
              <CollagePreviewTemplate
                className="web-gallery-collage-upsell-preview-img"
                collage={{
                  ...albumCustomPrint,
                  photos: {
                    total: samplePhotos.length,
                    records: samplePhotos.map((photo, index) => ({
                      position: index,
                      photo,
                    })),
                  },
                }}
              />
            )}
          </Fragment>
        )}
      </div>

      <div className="text-center p-2">
        <h3 className="text-extra-bold">
          <ContextualString
            id="webGalleryCollageUpsellModalTitle"
            defaultMessage="Save the Moment!"
            type={album.album_type}
            subtype={album.album_subtype}
          />
          <br />
          <ContextualString
            id="webGalleryCollageUpsellModalSubtitle"
            defaultMessage="Build your own custom 8x10 {collageName}!"
            type={album.album_type}
            subtype={album.album_subtype}
            values={{
              collageName: getAlbumCustomPrintName(albumCustomPrint),
            }}
          />
        </h3>

        <p>
          It&rsquo;s simple: Pick your <strong>{photosNo}</strong> fav pics.
          Choose your layout. Waldo will ship it to your home!
        </p>

        <div className="text-center max-w-50p mx-auto mt-3">
          <Button
            btnBlock
            to={`/galleries/view/album/${album.id}/custom-prints/checkout`}
            onClick={onConfirm}
          >
            I want one!
          </Button>

          <Button
            onClick={onDismiss}
            btnStyle="link"
            className="text-color-base mt-2"
            btnBlock
            btnSize="sm"
          >
            Maybe later
          </Button>
        </div>
      </div>
    </Fragment>
  );
};

WebGalleryCollageUpsell.propTypes = propTypes;

export default withModal(WebGalleryCollageUpsell, {
  name: WEB_GALLERY_COLLAGE_UPSELL_MODAL,
  hideHeader: true,
  dismissible: false,
  size: 'sm',
  className: 'web-gallery-collage-upsell-modal',
});
